import {
  type TAddOnCartItem,
  type TCartItem,
} from "@/components/Cart/types.cart";
import { createCartItemFromCard } from "@/components/Cart/utils";
import { isAddOnForFounder } from "@/components/Dashboard/v2/utils/isAddOnForFounder";
import { PLAN_REF_ID_MAPPING, PLANS_IN_BUNDLE } from "@/data/plans";
import { TBillingInterval } from "@/types/app";
import { TAddOnRefId, TProductRefId } from "@/types/plan";
import { TPlanPriceAmount } from "@/types/plan-data";
import { TAddOn, TPlan } from "../types.checkout";
import { TAddOnItem } from "../utils";
import { getPlanByProdRefId } from "./getPlanByProdRefId";

type TAddOnRuleMap = Partial<Record<TProductRefId, TAddOnRefId[]>>;

// ── Add-on filtering by rules ──

export function filterEligibleAddOns(
  addOnCards: TAddOn[],
  ownedPlanList: TPlan[],
  plans: TCartItem[],
  rulesMap: TAddOnRuleMap,
): TAddOn[] {
  const allPlanIds: TProductRefId[] = [
    ...ownedPlanList.map((p) => p.productRefId),
    ...plans.map((p) => p.id as TProductRefId),
  ];
  const allowedRefIds = new Set<TAddOnRefId>();
  for (const planId of allPlanIds) {
    const rules = rulesMap[planId];
    if (rules) for (const refId of rules) allowedRefIds.add(refId);
  }
  return addOnCards.filter((a) => allowedRefIds.has(a.productRefId));
}

/** Total quantity the user already owns, keyed by add-on productRefId. */
export function getOwnedAddOnQtyMap(
  ownedAddOnItemList: TAddOnItem[],
): Record<string, number> {
  return ownedAddOnItemList.reduce<Record<string, number>>((acc, item) => {
    acc[item.productRefId] = (acc[item.productRefId] ?? 0) + item.quantity;
    return acc;
  }, {});
}

/**
 * Drops add-ons the user has already maxed out. Each add-on can be owned up to
 * its `max_count`; once the owned quantity reaches it (e.g. a single-count
 * add-on the user already owns) the add-on is hidden.
 */
export function filterAvailableAddOns(
  addOns: TAddOn[],
  ownedAddOnItemList: TAddOnItem[],
): TAddOn[] {
  const ownedQtyMap = getOwnedAddOnQtyMap(ownedAddOnItemList);
  return addOns.filter(
    // A `null` `max_count` means no limit, so the add-on stays available.
    (addOn) =>
      (addOn.max_count ?? Infinity) - (ownedQtyMap[addOn.productRefId] ?? 0) >
      0,
  );
}

/**
 * Hides founder-only add-ons from non-founder users. An add-on is founder-only
 * when its `user_cohorts` targets the founder cohort (see {@link isAddOnForFounder}).
 */
export function filterFounderAddOns(
  addOns: TAddOn[],
  isFounder: boolean,
): TAddOn[] {
  return addOns.filter((addOn) => !isAddOnForFounder(addOn) || isFounder);
}

// ── Suggestions ──

export function resolveSuggestions(
  recommendationIds: TProductRefId[],
  planCards: TPlan[],
  billingPeriod: TBillingInterval,
): TCartItem[] {
  return (recommendationIds ?? [])
    .map((id) => getPlanByProdRefId(planCards, id))
    .filter((p): p is TPlan => p != null)
    .map((p) => createCartItemFromCard(p, billingPeriod, "plan"));
}

// ── Owned items ──

/**
 * Founders renew at a fixed price stored on `TUserPlanData.plan_price_amount`,
 * which diverges from the plan card's catalog pricing. When set, the override
 * replaces the catalog price for the matching owned plan (the founder's bundle)
 * and relabels it "Founders", mirroring the Dashboard's plan name treatment
 * (see SubscriptionList/useDashboard's "Max"/"Complete" -> "Founders" mapping).
 */
/** A founder's Complete renews at their own price, not the catalog's. */
export const getFounderPriceOverride = (
  isFounder: boolean | undefined,
  planPriceAmount?: TPlanPriceAmount,
) =>
  isFounder && planPriceAmount
    ? {
        productRefId: PLAN_REF_ID_MAPPING.all,
        price: planPriceAmount.price_in_dollars,
      }
    : undefined;

export function buildOwnedCartItems(
  ownedPlanList: TPlan[],
  billingPeriod: TBillingInterval,
  founderPriceOverride?: { productRefId: TProductRefId; price: number },
): TCartItem[] {
  return ownedPlanList.map((plan) => {
    const item = createCartItemFromCard(plan, billingPeriod, "plan");
    if (
      founderPriceOverride &&
      plan.productRefId === founderPriceOverride.productRefId
    ) {
      // The override isn't a discount off the catalog price — nothing to strike.
      return {
        ...item,
        name: "Founders",
        price: founderPriceOverride.price,
        listPrice: undefined,
      };
    }
    return item;
  });
}

export function buildOwnedAddOnCartItems(
  ownedAddOnItemList: TAddOnItem[],
  addOnCards: TAddOn[],
  billingPeriod: TBillingInterval,
): TAddOnCartItem[] {
  return ownedAddOnItemList
    .map((a) => {
      const card = addOnCards.find((ac) => ac.productRefId === a.productRefId);
      if (!card) return null;
      return createCartItemFromCard(card, billingPeriod, "add-on", a.quantity);
    })
    .filter((item): item is TAddOnCartItem => item !== null);
}

// ── Bundle expansion (for cancel dialog) ──

export function expandBundle(
  ownedPlanList: TPlan[],
  planCards: TPlan[],
): TPlan[] {
  const hasBundle = ownedPlanList.some(
    (p) => p.productRefId === PLAN_REF_ID_MAPPING.all,
  );
  if (!hasBundle) return ownedPlanList;
  return PLANS_IN_BUNDLE.map((refId) =>
    planCards.find((c) => c.productRefId === refId),
  ).filter((p): p is TPlan => Boolean(p));
}
